// src/mls-membership-probe.ts
/**
 * Map a `GET /api/v1/mls/groups/{id}` status onto "do I still hold state for
 * this group?" — LEAF MODULE (#629).
 *
 * `quarantineOrphanedMlsState` takes this decision as an injected dependency
 * precisely so its failure modes stay testable without a network. This is that
 * decision, isolated so the mapping itself is pinned.
 *
 * ⚠️ The load-bearing case is `null`. The quarantine treats `null` as "leave it
 * alone" and only acts on an explicit `false`, because a transient API failure
 * that looks like "you were removed" is how a working agent loses its history.
 * Every status we do not positively understand must therefore map to `null`,
 * never to `false`.
 *
 * Backend semantics (GET /api/v1/mls/groups/{id}):
 *   200  membership (or A2A observer) confirmed            -> keep
 *   404  no such group at all                              -> definitively gone
 *   403  "Not authorized for this MLS group"               -> definitively not ours
 *        (assert_group_member — the ONLY 403 that is a membership answer)
 *   403  "Device not found or not active"                  -> null
 *        (the companion backend change makes EVERY inactive-device auth failure
 *        this 403: a device PAUSED mid-session would otherwise quarantine every
 *        group it holds in one pass. It is a statement about the device, not
 *        about membership.)
 *   403  anything else / no detail                         -> null
 *   else unknown                                           -> null
 *
 * The 403 match is POSITIVE on purpose: only the exact not-a-member detail
 * authorises quarantine, so a future 403 reason cannot start renaming state.
 * The caller additionally confirms the device is ACTIVE before acting on that
 * `false` (channel.ts `_quarantineOrphanedMlsGroups`).
 *
 * 401 is deliberately `null`, not `false`: an expired device JWT is an auth
 * problem, not a statement about membership, and treating it as removal would
 * quarantine every group on the device at once.
 */

/** `true` keep · `false` quarantine · `null` unknown, do nothing. */
export type MembershipVerdict = boolean | null;

/** assert_group_member's detail — the one 403 that means "not a member". */
export const NOT_A_MEMBER_DETAIL = "Not authorized for this MLS group";

export function mlsMembershipFromStatus(status: number, detail?: string | null): MembershipVerdict {
  if (status === 200) return true;
  if (status === 404) return false;
  if (status === 403 && detail === NOT_A_MEMBER_DETAIL) return false;
  return null;
}
